import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Calculator,
  Home,
  TrendingUp,
  Shield,
  Umbrella,
  Target,
  PiggyBank,
  Banknote,
  Search,
  Star,
  Clock,
} from "lucide-react";

interface CalculatorCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: string;
  popular?: boolean;
  comingSoon?: boolean;
}

interface CalculatorsHeaderProps {
  activeCalculator: string;
}

const calculators: CalculatorCard[] = [
  {
    id: "incometax",
    title: "Income Tax Calculator",
    description: "Calculate your income tax for FY 2026-27 with our smart questionnaire",
    icon: Calculator,
    category: "Tax Planning",
    popular: true,
  },
  {
    id: "homeloan",
    title: "Home Loan EMI",
    description: "Calculate your home loan EMI, total interest, and repayment schedule",
    icon: Home,
    category: "Loans & EMI",
  },
  {
    id: "retirement",
    title: "Retirement Planner",
    description: "Plan your retirement corpus and monthly SIP requirements",
    icon: TrendingUp,
    category: "Investment Planning",
    popular: true,
  },
  {
    id: "insurance",
    title: "Insurance Calculator",
    description: "Calculate life and health insurance coverage needed for your family",
    icon: Shield,
    category: "Insurance Planning",
  },
  {
    id: "emergency",
    title: "Emergency Fund",
    description: "Calculate the ideal emergency fund for your financial security",
    icon: Umbrella,
    category: "Financial Planning",
    popular: true,
  },
  {
    id: "goals",
    title: "Goal Planning",
    description: "Plan and track your financial goals with SIP calculations",
    icon: Target,
    category: "Investment Planning",
  },
  {
    id: "pf",
    title: "PF Calculator",
    description: "Calculate your Provident Fund corpus and withdrawal amount",
    icon: PiggyBank,
    category: "Tax Planning",
  },
  {
    id: "ppf",
    title: "PPF Calculator",
    description: "Calculate PPF returns, maturity amount and tax savings",
    icon: Banknote,
    category: "Tax Planning",
    popular: true,
  },
  {
    id: "sip",
    title: "SIP Calculator",
    description: "Calculate SIP returns and plan your mutual fund investments",
    icon: TrendingUp,
    category: "Investment Planning",
    popular: true,
  },
];

const categories = [
  "All",
  "Tax Planning",
  "Investment Planning",
  "Loans & EMI",
  "Insurance Planning",
  "Financial Planning",
];

/**
 * Modern calculator selection interface with search, categories, and card-based layout
 */
export function CalculatorsHeader({ activeCalculator }: Readonly<CalculatorsHeaderProps>) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const openCalculator = (calc: CalculatorCard) => {
    if (calc.comingSoon) {
      return;
    }
    if (calc.id === "goals") {
      navigate("/goals");
      return;
    }
    navigate(`/calculators/${calc.id}`);
  };

  const filteredCalculators = calculators.filter((calc) => {
    const matchesSearch =
      calc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      calc.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || calc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const popularCalculators = calculators.filter((calc) => calc.popular && !calc.comingSoon);

  const sectionTitle = (() => {
    if (searchQuery) {
      return `Search Results (${filteredCalculators.length})`;
    }
    if (selectedCategory === "All") {
      return "All Calculators";
    }
    return selectedCategory;
  })();

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-2 sm:space-y-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
          Financial Calculators
        </h1>
        <p className="text-sm sm:text-base md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
          Make informed financial decisions with our comprehensive suite of calculators. Plan your
          taxes, investments, loans, and financial goals with ease.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search calculators..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <Button
                key={category}
                variant={isActive ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="text-xs sm:text-sm"
                aria-pressed={isActive}
                data-state={isActive ? "active" : "inactive"}
              >
                {category}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Popular Calculators */}
      {!searchQuery && selectedCategory === "All" && (
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">Popular Calculators</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {popularCalculators.map((calc) => (
              <CalculatorCard
                key={calc.id}
                calculator={calc}
                isActive={activeCalculator === calc.id}
                onClick={() => openCalculator(calc)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Calculators */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold">{sectionTitle}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {filteredCalculators.map((calc) => (
            <CalculatorCard
              key={calc.id}
              calculator={calc}
              isActive={activeCalculator === calc.id}
              onClick={() => openCalculator(calc)}
            />
          ))}
        </div>

        {filteredCalculators.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <Calculator className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium mb-2">No calculators found</h3>
            <p className="text-sm sm:text-base text-muted-foreground px-4">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

interface CalculatorCardProps {
  calculator: CalculatorCard;
  isActive: boolean;
  onClick: () => void;
}

/**
 * Calculator Card.
 */
function CalculatorCard({ calculator, isActive, onClick }: Readonly<CalculatorCardProps>) {
  const Icon = calculator.icon;

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
        isActive ? "ring-2 ring-primary shadow-lg" : ""
      } ${calculator.comingSoon ? "opacity-75" : ""}`}
      onClick={calculator.comingSoon ? undefined : onClick}
    >
      <CardHeader className="pb-2 sm:pb-3 p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div
              className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${isActive ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}
            >
              <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-sm sm:text-base md:text-lg truncate">
                {calculator.title}
              </CardTitle>
              <Badge variant="outline" className="text-[10px] sm:text-xs mt-1">
                {calculator.category}
              </Badge>
            </div>
          </div>

          <div className="flex flex-col gap-1 flex-shrink-0">
            {calculator.popular && (
              <Badge variant="secondary" className="text-[10px] sm:text-xs px-1 sm:px-2">
                <Star className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                Popular
              </Badge>
            )}
            {calculator.comingSoon && (
              <Badge variant="outline" className="text-[10px] sm:text-xs px-1 sm:px-2">
                <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                Soon
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3 pt-0 sm:p-4 sm:pt-0">
        <CardDescription className="text-xs sm:text-sm leading-relaxed line-clamp-2 sm:line-clamp-none">
          {calculator.description}
        </CardDescription>

        {!calculator.comingSoon && (
          <Button
            className="w-full mt-3 sm:mt-4 h-8 sm:h-9 text-xs sm:text-sm"
            variant={isActive ? "default" : "outline"}
            size="sm"
          >
            {isActive ? "Active" : "Use Calculator"}
          </Button>
        )}

        {calculator.comingSoon && (
          <Button
            className="w-full mt-3 sm:mt-4 h-8 sm:h-9 text-xs sm:text-sm"
            variant="outline"
            size="sm"
            disabled
          >
            Coming Soon
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
